import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchWalletSnapshot, type WalletSnapshot } from '../api/payments';
import { useAuth } from './AuthContext';

const POLL_MS = 3000;

type WalletLiveValue = {
  wallet: WalletSnapshot | null;
  refreshWallet: () => Promise<WalletSnapshot | null>;
  setWalletSnapshot: (snap: WalletSnapshot | null) => void;
};

const WalletLiveContext = createContext<WalletLiveValue | null>(null);

export function WalletLiveProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const inFlight = useRef(false);
  const userId = user?.public_id || null;

  const refreshWallet = useCallback(async () => {
    if (!userId) {
      setWallet(null);
      return null;
    }
    if (inFlight.current) return null;
    inFlight.current = true;
    try {
      const snap = await fetchWalletSnapshot();
      const next = snap.authenticated ? snap : null;
      setWallet(next);
      return next;
    } catch {
      return null;
    } finally {
      inFlight.current = false;
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setWallet(null);
      return;
    }

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void refreshWallet();
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [userId, refreshWallet]);

  const value = useMemo(
    () => ({
      wallet,
      refreshWallet,
      setWalletSnapshot: setWallet,
    }),
    [wallet, refreshWallet],
  );

  return <WalletLiveContext.Provider value={value}>{children}</WalletLiveContext.Provider>;
}

export function useWalletLive(): WalletLiveValue {
  const ctx = useContext(WalletLiveContext);
  if (!ctx) {
    throw new Error('useWalletLive must be used within WalletLiveProvider');
  }
  return ctx;
}
